import Avatar from '@components/avatar';
import { buildFullName } from '@hilbi/web-common/utils/common';
import { PersonFilledOutlined, PlusOutlined } from '@icon';
import { MenuItemType, StatusBadge, Table, TableProps, Tag, TTableInitialState } from '@ui';
import React from 'react';
import { useIntl } from 'react-intl';
import { Link, useHistory } from 'react-router-dom';
import type { ColumnWithLooseAccessor } from 'react-table';
import { IClientSearchItem, IClientSearchParams } from 'types/client.types';
import { Full } from 'types/common';
import { PatientListAction } from 'types/patients';

type TColumn = ColumnWithLooseAccessor<IClientSearchItem>;

export type TTableProps = Full<TableProps<IClientSearchItem>>;

export const INITIAL_PARAMS: IClientSearchParams = {
  myClients: true,
  page: 1,
  pageSize: 30,
  sortBy: 'lastName',
  direction: 'ASC',
  status: 'paired',
};

export const INITIAL_STATE: TTableInitialState<IClientSearchItem> = {
  pageSize: 30,
  pageIndex: 0,
  sortBy: [{ id: 'lastName', desc: false }],
};

type TColumnsParams = {
  allClientsShown: boolean;
  onPairClick: (e: React.MouseEvent<Element, MouseEvent>, client: IClientSearchItem) => void;
};

export function useTableColumns(params: TColumnsParams) {
  const { formatMessage } = useIntl();
  const { push } = useHistory();
  const { allClientsShown, onPairClick } = params;

  return React.useMemo<TColumn[]>(() => {
    return [
      {
        Header: '',
        id: 'avatar',
        Cell: ({ row: { original } }: any) => <Avatar imgUrl={original.profile.profileImage} />,
      },
      {
        Header: formatMessage({ id: 'profile.name' }),
        id: 'lastName',
        accessor: 'lastName' as any,
        disableSortBy: false,
        Cell: ({ row }) => {
          const { titleBeforeName, firstName, lastName, titleAfterName } = row.original.profile;
          return (
            <Link
              to={`/life-id/${row.original.id}`}
              className={'name-link'}
              style={{ whiteSpace: 'nowrap' }}
            >
              {buildFullName(titleBeforeName, firstName, lastName, titleAfterName)}
              {row.original.myClient && allClientsShown ? (
                <StatusBadge variant="info" size="small" style={{ marginLeft: 8 }} />
              ) : null}
            </Link>
          );
        },
      },
      {
        Header: 'Rodné číslo',
        id: 'pin',
        Cell: ({ row: { original } }) => <>{`${original.profile.pin ?? '-'}`}</>,
      },
      {
        Header: 'Tel. č.',
        id: 'email',
        Cell: ({ row: { original } }) => <>{`${original.phoneNumber ?? '-'}`}</>,
      },
      {
        id: 'pendingActions',
        disableSortBy: true,
        Cell: ({ row }) => {
          if (!row.values.pendingActions) {
            return null;
          }

          return row.values.pendingActions.map((action: PatientListAction, i: number) => {
            if (!row.original.jabberId || !row.original.id) {
              throw new Error('missing required params for navigation');
            }

            const openLifeId = () => push(`/life-id/${row.original.id}/TOOLS`);
            return (
              <Tag key={i} title={action.payload.name} onClick={openLifeId} variant="accent" />
            );
          });
        },
      },
      {
        Header: '',
        id: 'actions',
        Cell: ({ row }) => {
          const actions: MenuItemType[] = [
            {
              key: 'detail',
              label: 'Zobraziť detail',
              Icon: PersonFilledOutlined,
              onClick: () => {
                push(`/life-id/${row.original.id}`);
              },
            },
          ];

          if (!row.original.myClient) {
            actions.push({
              key: 'addClient',
              label: 'Pridať medzi svojich klientov',
              Icon: PlusOutlined,
              onClick: (e) =>
                onPairClick(e.domEvent as React.MouseEvent<HTMLElement, MouseEvent>, row.original),
              variant: 'success',
            });
          }

          return <Table.Actions items={actions} />;
        },
      },
    ];
  }, [formatMessage, allClientsShown]);
}
